import {
  ILoyaltyServiceQuestFragment,
  IQuestFragment,
  LoyaltyCmsType,
  LoyaltyQuestIncentiveType,
  LoyaltyQuestStatus,
  LoyaltyQuestStepStatus,
  LoyaltyServiceCmsType,
  LoyaltyServiceQuestIncentiveType,
  LoyaltyServiceQuestStatus,
  LoyaltyServiceQuestStepStatus,
  LoyaltyServiceQuestType,
} from 'generated/graphql-gateway';

import { IQuest, IQuestStep, QuestCmsType, QuestError, QuestValidationError } from './types';

export const validateQuest = (quest: IQuest): QuestError[] => {
  const errors: QuestError[] = [];

  if (!quest?.cmsId) {
    errors.push({
      error: QuestValidationError.INVALID_CMS_DOCUMENT,
      message: `No cms document [${quest.id}]`,
    });
  }

  return errors;
};

export const createQuest = ({
  id,
  type,
  incentiveType,
  endDate,
  startDate,
  sanityId,
  externalIdentifiers,
  status,
  steps,
  bonusPoints,
  lastCompletion,
  cmsType,
}: IQuestFragment): IQuest => ({
  cmsId: sanityId || '',
  cmsType: (externalIdentifiers?.find(e => e?.value === sanityId)?.type as QuestCmsType) || '',
  type,
  startDate,
  endDate,
  id,
  incentiveType,
  lastCompletion: lastCompletion || null,
  status,
  steps: (steps as IQuestStep[]) || null,
  bonusPoints,
  loyaltyCmsType: cmsType ?? null,
});

const incentiveTypeByServiceType: Record<
  LoyaltyServiceQuestIncentiveType,
  LoyaltyQuestIncentiveType
> = {
  [LoyaltyServiceQuestIncentiveType.BADGE]: LoyaltyQuestIncentiveType.BADGE,
  [LoyaltyServiceQuestIncentiveType.BONUS_POINTS]: LoyaltyQuestIncentiveType.BONUS_POINTS,
  [LoyaltyServiceQuestIncentiveType.OFFER]: LoyaltyQuestIncentiveType.OFFER,
  [LoyaltyServiceQuestIncentiveType.REWARD]: LoyaltyQuestIncentiveType.REWARD,
};

const statusByServiceStatus: Record<LoyaltyServiceQuestStatus, LoyaltyQuestStatus> = {
  [LoyaltyServiceQuestStatus.ACTIVATED]: LoyaltyQuestStatus.ACTIVATED,
  [LoyaltyServiceQuestStatus.COMPLETED]: LoyaltyQuestStatus.COMPLETED,
  [LoyaltyServiceQuestStatus.NOT_COMPLETED]: LoyaltyQuestStatus.NOT_COMPLETED,
  [LoyaltyServiceQuestStatus.NOT_ENROLLED]: LoyaltyQuestStatus.NOT_ENROLLED,
};

const stepStatusByServiceStatus: Record<LoyaltyServiceQuestStepStatus, LoyaltyQuestStepStatus> = {
  [LoyaltyServiceQuestStepStatus.COMPLETED]: LoyaltyQuestStepStatus.COMPLETED,
  [LoyaltyServiceQuestStepStatus.NOT_COMPLETED]: LoyaltyQuestStepStatus.NOT_COMPLETED,
};

const cmsTypeByServiceCmsType: Record<LoyaltyServiceCmsType, LoyaltyCmsType> = {
  [LoyaltyServiceCmsType.EVERGREEN]: LoyaltyCmsType.EVERGREEN,
  [LoyaltyServiceCmsType.MONTHLY]: LoyaltyCmsType.MONTHLY,
  [LoyaltyServiceCmsType.REGULAR]: LoyaltyCmsType.REGULAR,
  [LoyaltyServiceCmsType.USER_JOURNEY]: LoyaltyCmsType.USER_JOURNEY,
};

const getQuestCmsType = (quest: ILoyaltyServiceQuestFragment): QuestCmsType | '' => {
  const renderableType =
    quest.renderable && typeof quest.renderable === 'object' ? quest.renderable._type : undefined;

  if (renderableType === QuestCmsType.CONFIG || renderableType === QuestCmsType.SYSTEMWIDE) {
    return renderableType;
  }

  if (quest.type === LoyaltyServiceQuestType.CONFIG) {
    return QuestCmsType.CONFIG;
  }

  if (quest.type === LoyaltyServiceQuestType.GLOBAL) {
    return QuestCmsType.SYSTEMWIDE;
  }

  return '';
};

const createServiceQuestSteps = (
  serviceSteps: ILoyaltyServiceQuestFragment['steps']
): IQuestStep[] | null => {
  if (!serviceSteps) {
    return null;
  }

  const steps = serviceSteps.map<IQuestStep>(step => ({
    id: step.id,
    status: stepStatusByServiceStatus[step.status],
    cartRequirements: step.cartRequirements?.reduce<NonNullable<IQuestStep['cartRequirements']>>(
      (groups, group) => {
        if (group) {
          groups.push(
            group.flatMap(requirement =>
              requirement ? [{ id: requirement.id, quantity: requirement.quantity }] : []
            )
          );
        }
        return groups;
      },
      []
    ),
    field: step.field,
    bonusPoints: step.bonusPoints,
    tierIndex: step.tierIndex,
  }));

  return steps;
};

export const createServiceQuest = (quest: ILoyaltyServiceQuestFragment): IQuest => ({
  id: quest.id,
  type: quest.type,
  startDate: quest.startDate,
  endDate: quest.endDate,
  incentiveType: incentiveTypeByServiceType[quest.incentiveType],
  cmsId: quest.sanityId || quest.cmsId || '',
  cmsType: getQuestCmsType(quest),
  status: statusByServiceStatus[quest.status],
  lastCompletion: quest.lastCompletion
    ? {
        benefitId: quest.lastCompletion.benefitId,
        completedAt: quest.lastCompletion.completedAt,
        redeemed: quest.lastCompletion.redeemed,
      }
    : null,
  steps: createServiceQuestSteps(quest.steps),
  bonusPoints: quest.bonusPoints,
  loyaltyCmsType: quest.cmsType ? cmsTypeByServiceCmsType[quest.cmsType] : null,
});
